/**
 * 이 파일은 Aui 의 일부입니다. (https://aui.moimz.tools)
 *
 * 메뉴의 커스텀아이템 클래스를 정의한다.
 *
 * @file /src/components/menu/elements/items/Custom.ts
 * @author Arzz <arzz@arzz.com>
 * @license MIT License
 * @modified 2026. 5. 15.
 */
import * as parent from '../Item';
import { Component } from '@base/Component';
import { Dom } from '@utils/Dom';
import { Html } from '@utils/Html';

export interface Listeners<T> extends parent.Listeners<T> {}

export interface Properties<T> extends parent.Properties<T> {
    /**
     * 메뉴타입
     */
    type: 'custom';

    /**
     * 메뉴 커스텀 컴포넌트
     */
    custom: Component;

    /**
     * 메뉴 아이콘
     */
    icon?: string;

    /**
     * 이벤트리스너
     */
    listeners?: Listeners<T>;
}

export class Custom extends parent.Item {
    /**
     * 메뉴아이템 타입
     */
    variant: string = 'custom';

    /**
     * 메뉴 커스텀 컴포넌트
     */
    custom: Component = null;

    /**
     * 메뉴 아이콘 스타일시트 클래스
     */
    icon: string | null = null;

    /**
     * 아이콘 DOM
     */
    $icon: Dom = null;

    /**
     * 커스텀 영역 DOM
     */
    $custom: Dom = null;

    /**
     * 커스덤 메뉴 아이템을 정의한다.
     *
     * @param properties - 초기설정
     */
    constructor(public properties: Properties<Custom>) {
        super(properties);

        this.icon = this.properties?.icon ?? null;
        this.custom = this.properties?.custom ?? null;
    }

    /**
     * 부모 컴포넌트를 설정한다.
     *
     * @param component - 현재 엘리먼트를 가진 컴포넌트
     */
    setComponent(component: Component): void {
        this.component = component;
        this.custom?.setParent(this.component);
    }

    /**
     * 아이콘 DOM 을 가져온다.
     *
     * @returns 아이콘 DOM
     */
    $getIcon(): Dom {
        if (this.$icon === null) {
            this.$icon = Html.create('i');
        }

        return this.$icon;
    }

    /**
     * 커스텀 영역 DOM을 가져온다.
     *
     * @returns 커스텀 영역 DOM
     */
    $getCustom(): Dom {
        if (this.$custom === null) {
            this.$custom = Html.create('div');
        }

        return this.$custom;
    }

    /**
     * 아이콘을 변경한다.
     *
     * @param icon - 변경할 아이콘 (NULL 인 경우 아이콘을 제거한다.)
     */
    setIcon(icon: string | null = null): void {
        this.icon = icon;

        const $icon = this.$getIcon();
        $icon.removeClass();

        if (this.icon !== null) {
            $icon.addClass(...this.icon.split(' '));
        }
    }

    /**
     * 메뉴 아이템에 포커스를 지정한다.
     */
    focus(): boolean {
        this.custom.focus();
        return true;
    }

    /**
     * 커스텀 컴포넌트를 렌더링한다.
     *
     * @param is_force - 렌더링이 당장 필요없는 상태라도, 강제로 렌더링할지 여부
     */
    async renderCustom(is_force: boolean = false): Promise<void> {
        const $children = document.createDocumentFragment();
        $children.append(this.custom.$getComponent().getEl());

        this.$getCustom().append($children);
        await this.custom.render(is_force);
    }

    /**
     * 엘리먼트를 렌더링한다.
     */
    async render(): Promise<void> {
        if (this.isRendered() == true) {
            return;
        }

        if (this.isHidden() == true) {
            this.hide();
        }

        this.$getElement().addClass('custom');

        if (this.icon) {
            this.setIcon(this.icon);
            this.$getElement().append(this.$getIcon());
        }

        this.$getElement().append(this.$getCustom());
        await this.renderCustom();

        await this.onRender();
    }

    /**
     * 엘리먼트를 제거한다.
     *
     * @param removeDom - 엘리먼트 DOM 을 제거할지 여부
     */
    async remove(removeDom: boolean = true): Promise<void> {
        await this.custom?.remove(false);
        this.custom = null;
        this.$icon = null;
        this.$custom = null;
        await super.remove(removeDom);
    }
}
